/* Complete documentation for the useSwitcherClient hook can be found at:
    https://app.shortcut.com/switcher-studio/write/IkRvYyI6I3V1aWQgIjY3MzUyYjEyLTkyMDQtNGU1OC05YWM5LTBmNGI3OWIzM2M3NSI=
*/

import { useCallback, useEffect, useState, useContext, useRef } from "react";
import { Client } from "@switcherstudio/switcher-api-client";
import { Client as ClientCore } from "@switcherstudio/api-core-client";
import { SwitcherClientContext } from "App";
import { isEqual } from "lodash";

/* Types */
type AsyncReturnType<T extends Promise<any>> = T extends Promise<infer R>
    ? R
    : any;

/**
 * Options for the useSwitcherClient hook
 * @param X - The arguments to pass to the clientMethod
 * @param U - The response type of the clientMethod
 * @param Y - The transformed response type
 */
interface SwitcherClientOptions<X, U, Y> {
    /**
     * The arguments to pass to the clientMethod. If args change during component
     * lifecycle and "requestImmediately" is set to true a new API request will be triggered
     */
    args?: X;
    /**
     * Whether to initiate the request immediately upon component mount.
     * Optional - defaults to false.
     * Note: This does not prevent automatic re-fetching of data when args change
     */
    requestImmediately?: boolean;
    preFetch?: () => void;
    /**
     * @param data The response from the API request
     * @returns Void or Promise of void
     *
     * A callback function to call after the API request succeeds.
     * Can be synchronous or asynchronous
     */
    onSuccess?: (data: U, transformedData: Y) => Promise<void> | void;
    /**
     * @param e Error from API request response
     * @returns Void
     *
     * A callback function to call when the API request fails.
     */
    onError?: (e: any, response?: U) => void;
    /** Delays termination of global loading animation
     *  until onSuccess returns (synchronously or asynchronously)
     */
    onSuccessLoading?: boolean;
    /**
     * Optional function that transforms the response data before passing it to onSuccess.
     * This transformation happens after the local loading is set to false, but before the loading state is set to false.
     * This data is exposed via the exported transformedData variable.
     */
    transformResponseData?: ({
        originalResponse,
        originalArgs
    }: {
        originalResponse: U;
        originalArgs: X;
    }) => Promise<Y> | Y;
}

export function useSwitcherClientCore<U, X extends any[], Y>(
    clientMethod: (client: ClientCore) => (...P: X) => Promise<U>,
    options?: SwitcherClientOptions<X, U, Y>
) {
    return useSwitcherClientInternal<ClientCore, U, X, Y>(
        clientMethod,
        options,
        true
    );
}

export function useSwitcherClient<U, X extends any[], Y>(
    clientMethod: (client: Client) => (...P: X) => Promise<U>,
    options?: SwitcherClientOptions<X, U, Y>
) {
    return useSwitcherClientInternal<Client, U, X, Y>(
        clientMethod,
        options,
        false
    );
}

/**
 * Internal hook for useSwitcherClient and useSwitcherClientCore
 */
function useSwitcherClientInternal<
    A extends Client | ClientCore,
    U,
    X extends any[],
    Y
>(
    /**
     * The Switcher Client method to be invoked.
     * Requires method definition (rather than the immediately invoked method)
     */
    clientMethod: (client: A) => (...P: X) => Promise<U>,
    options?: SwitcherClientOptions<X, U, Y>,
    useCore: boolean = false
) {
    /* Local Types */
    interface CancelablePromise {
        promise: Promise<U>;
        cancel: (...args: any) => any;
    }

    /* State */

    // Context
    const clientContext = useContext(SwitcherClientContext);
    const switcherClient = useCore
        ? clientContext.switcherClientCore
        : clientContext.switcherClient;

    // Local
    const [data, setData] = useState<AsyncReturnType<Promise<U>>>(null);
    const [transformedData, setTransformedData] = useState<Y>(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(
        options?.requestImmediately ?? false
    );
    const [isInitialRequest, setisInitialRequest] = useState(true);
    const cancelablePromise = useRef<CancelablePromise>(null);

    // References
    const argsRef = useRef({ clientMethod, options });
    const updatedArgsRef = useRef<X>(null);

    const makeCancelable = useCallback((promise): CancelablePromise => {
        let _hasCanceled = false;

        const wrappedPromise = new Promise<U>((resolve, reject) => {
            promise.then(
                (val) => (_hasCanceled ? resolve(null) : resolve(val)),
                (error) =>
                    _hasCanceled ? reject({ isCanceled: true }) : reject(error)
            );
        });

        wrappedPromise.catch(() => {});

        return {
            promise: wrappedPromise,
            cancel() {
                _hasCanceled = true;
            }
        };
    }, []);

    const handleResponse = useCallback(async (response: U, updatedArgs?: X) => {
        setData(response);

        let transformedRes: Y;
        if (!!argsRef.current?.options?.transformResponseData) {
            transformedRes = await Promise.resolve(
                argsRef.current.options.transformResponseData({
                    originalResponse: response,
                    originalArgs: updatedArgs ?? argsRef.current.options?.args
                })
            );
            setTransformedData(transformedRes);
        }
        setLoading(false);
        setError(null);
        if (!!argsRef.current.options?.onSuccess) {
            if (argsRef.current.options.onSuccessLoading) {
                Promise.resolve(
                    argsRef.current.options.onSuccess(response, transformedRes)
                );
            } else {
                argsRef.current.options.onSuccess(response, transformedRes);
            }
        }

        return response;
    }, []);

    const request = useCallback(
        async (updatedArgs?: X) => {
            updatedArgsRef.current = updatedArgs;

            setLoading(true);

            !!argsRef.current.options?.preFetch &&
                argsRef.current.options?.preFetch();

            try {
                cancelablePromise.current = makeCancelable(
                    argsRef.current
                        .clientMethod(switcherClient as unknown as A)
                        .apply(
                            switcherClient,
                            updatedArgs?.length
                                ? updatedArgs
                                : argsRef.current.options?.args ?? ([] as X)
                        )
                );
                cancelablePromise.current.promise
                    .then((res) => {
                        handleResponse(res, updatedArgs);
                    })
                    .catch((e) => {
                        if (!e.isCanceled) {
                            setError(e);
                            setLoading(false);
                            if (!!argsRef.current.options?.onError) {
                                if (!!e?.response) {
                                    try {
                                        const parsedResponse = JSON.parse(
                                            e.response
                                        ) as U;
                                        argsRef.current.options.onError(
                                            e,
                                            parsedResponse
                                        );
                                    } catch (parseError) {
                                        argsRef.current.options.onError(e);
                                    }
                                } else {
                                    argsRef.current.options.onError(e);
                                }
                            }
                        }
                    });

                return cancelablePromise.current.promise;
            } catch (e) {
                if (!e.isCanceled) {
                    setError(e);
                    setLoading(false);
                    if (!!argsRef.current.options?.onError) {
                        argsRef.current.options.onError(e);
                    }
                    // re-throw error to be caught by the caller in cases where we have try/catch blocks around an await call
                    throw e;
                }
            }
        },
        [handleResponse, makeCancelable, switcherClient]
    );

    const dispatchApiRequest = useCallback(
        (updatedArgs?: X) => {
            return request(updatedArgs);
        },
        [request]
    );

    /* Effects */

    // update args on args dependency change and re-call request if requestImmediately is true
    useEffect(() => {
        if (
            !!options &&
            !isEqual(options?.args, argsRef?.current?.options?.args) &&
            argsRef.current.options?.requestImmediately
        ) {
            request(options.args);
        }

        argsRef.current.options = options;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);

    // cancel request on unmount
    useEffect(() => {
        return () => {
            cancelablePromise.current && cancelablePromise.current.cancel();
        };
    }, []);

    useEffect(() => {
        if (
            !!argsRef.current.clientMethod &&
            !argsRef.current?.options?.args?.includes(undefined) &&
            argsRef.current.options?.requestImmediately &&
            isInitialRequest
        ) {
            setisInitialRequest(false);
            request();
        }
    }, [isInitialRequest, request]);

    /* Return hook values */
    return {
        data,
        error,
        loading,
        dispatchApiRequest,
        transformedData
    };
}
